import React, { useCallback } from "react";
import { HiOutlineCubeTransparent, HiOutlineTicket } from "react-icons/hi2";
import { useLocation, useParams } from "react-router-dom";
import { useSearch } from "../../../../contexts/SearchContext";
import { useBookingFlow } from "../hooks/useBookingFlow";
import { isAgentFlightResultsListUrl } from "../../flightBookingPathUtils";
import "./FlightResultsModeToggle.css";

const DEBUG_ENDPOINT =
  "http://127.0.0.1:7626/ingest/ca460569-320b-4741-be20-10cc54aa7608";

function postDebugLog({ runId, hypothesisId, location, message, data }) {
  if (typeof import.meta === "undefined" || !import.meta.env?.DEV) return;
  // #region agent log
  fetch(DEBUG_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Debug-Session-Id": "c6e397",
    },
    body: JSON.stringify({
      sessionId: "c6e397",
      runId,
      hypothesisId,
      location,
      message,
      data,
      timestamp: Date.now(),
    }),
  }).catch(() => {});
  // #endregion
}

/**
 * במקום כותרת סטטית לשלב תוצאות — מעבר בין רשימת עסקאות להרכבת מסלול ידנית.
 */
export default function FlightResultsModeToggle() {
  const { pathname } = useLocation();
  const { flightStep } = useParams();
  const {
    currentStep,
    flowType,
    goToStep,
    searchGate,
    clearSelectedFlightOffer,
    openResultsAfterPlanesSearch,
  } = useBookingFlow();
  const {
    loading,
    setSelectedSelfComposeOutboundDeal,
    setSelectedSelfComposeInboundDeal,
  } = useSearch();

  const allowSelf = !!searchGate?.allowSelfComposeChoice;
  const onResultsListUrl = isAgentFlightResultsListUrl(pathname, flightStep);
  const onResults =
    currentStep === "results" ||
    (currentStep === "search" && (onResultsListUrl || loading));
  const onSelfCompose = currentStep === "selfComposePick";

  const resetSelfComposeSearch = useCallback(() => {
    setSelectedSelfComposeOutboundDeal(null);
    setSelectedSelfComposeInboundDeal(null);
  }, [setSelectedSelfComposeOutboundDeal, setSelectedSelfComposeInboundDeal]);

  const handleResults = useCallback(() => {
    resetSelfComposeSearch();
    clearSelectedFlightOffer();
    goToStep("results");
  }, [goToStep, clearSelectedFlightOffer, resetSelfComposeSearch]);

  const handleSelfCompose = useCallback(() => {
    if (!allowSelf) return;

    // #region agent log
    postDebugLog({
      runId: "run2",
      hypothesisId: "H1",
      location: "FlightResultsModeToggle.jsx:73",
      message: "[FLIGHT_SELF_COMPOSE_PATH]",
      data: {
        action: "handleSelfCompose_entry",
        pathname,
        currentStep,
        onResults,
        onSelfCompose,
        loading,
        flowTypeSnapshot: flowType ?? null,
      },
    });
    // #endregion

    if (import.meta.env.DEV) {
      console.log("[SELF_COMPOSE_CTA_CLICK]", {
        pathname,
        currentStep,
        loading,
        flowTypeSnapshot: flowType ?? null,
        targetStep: "selfComposePick",
        targetMode: "selfComposePick",
        allowSelfComposeChoice: Boolean(searchGate?.allowSelfComposeChoice),
        needsSelfComposePickStep: Boolean(searchGate?.needsSelfComposePickStep),
      });
    }

    /**
     * canEnterStep(selfComposePick) דורש flowType !== null.
     * במצב …/flights/results עם currentStep עדיין "search", flowType לעיתים עדיין null עד סנכרון —
     * openResultsAfterPlanesSearch מאתחל flowType + results כמו אחרי חיפוש (ללא חיפוש חדש).
     */
    clearSelectedFlightOffer();
    resetSelfComposeSearch();
    openResultsAfterPlanesSearch();
    // #region agent log
    postDebugLog({
      runId: "run2",
      hypothesisId: "H1",
      location: "FlightResultsModeToggle.jsx:104",
      message: "[FLIGHT_SELF_COMPOSE_PATH]",
      data: {
        action: "called_openResultsAfterPlanesSearch_before_selfCompose",
        pathnameBeforeGoTo: window.location.pathname,
        currentStepBeforeGoTo: currentStep,
      },
    });
    // #endregion
    goToStep("selfComposePick");
    // #region agent log
    postDebugLog({
      runId: "run2",
      hypothesisId: "H1",
      location: "FlightResultsModeToggle.jsx:116",
      message: "[FLIGHT_SELF_COMPOSE_PATH]",
      data: {
        action: "called_goToStep_selfComposePick",
        pathnameAfterGoToCall: window.location.pathname,
      },
    });
    // #endregion
  }, [
    allowSelf,
    clearSelectedFlightOffer,
    currentStep,
    flowType,
    goToStep,
    loading,
    openResultsAfterPlanesSearch,
    onResults,
    onSelfCompose,
    pathname,
    resetSelfComposeSearch,
    searchGate?.allowSelfComposeChoice,
    searchGate?.needsSelfComposePickStep,
  ]);

  const selfComposeTitle = !allowSelf
    ? searchGate?.partialSelfCompose
      ? "חסר כיוון אחד בתוצאות — לא ניתן להרכיב מסלול מלא"
      : !searchGate?.parallelOk
        ? "זמין אחרי חיפוש הלוך-חזור (כולל חיפוש מקבילי)"
        : "אין רשימות הלוך וחזור נפרדות לבחירה"
    : undefined;

  return (
    <header className="fw-results-mode-toggle" aria-label="מצב תצוגת תוצאות">
      <div className="fw-results-mode-toggle__inner" role="group">
        <button
          type="button"
          className={`fw-results-mode-toggle__btn${onResults ? " fw-results-mode-toggle__btn--active" : ""}`}
          onClick={handleResults}
          aria-pressed={onResults}
        >
          <span className="fw-results-mode-toggle__icon" aria-hidden>
            <HiOutlineTicket strokeWidth={2} />
          </span>
          <span className="fw-results-mode-toggle__label">תוצאות</span>
        </button>
        <button
          type="button"
          className={`fw-results-mode-toggle__btn${onSelfCompose ? " fw-results-mode-toggle__btn--active" : ""}`}
          onClick={handleSelfCompose}
          aria-pressed={onSelfCompose}
          disabled={!allowSelf}
          title={selfComposeTitle || undefined}
        >
          <span className="fw-results-mode-toggle__icon" aria-hidden>
            <HiOutlineCubeTransparent strokeWidth={2} />
          </span>
          <span className="fw-results-mode-toggle__label">הרכב לבד</span>
        </button>
      </div>
    </header>
  );
}
